/**
 * Normalization functions that convert book-idp's current contract shapes
 * into the legacy IIDPField union consumed by UI rendering components.
 *
 * This adapter layer lets rendering components stay unchanged while the
 * backend contract evolves.
 */
import {
  EIDPElementType,
  type IIDPBoundingBox,
  type IIDPExtractedField,
  type IIDPField,
  type IIDPKeyValue,
  type IIDPListBlock,
  type IIDPTable,
  type IIDPTextBlock,
} from '../types';

type RawElement = Record<string, unknown>;

/**
 * Extracts a bounding box from a raw element, returning null if absent.
 * Supports both the current format ({x, y, width, height}) and the legacy
 * Location format ({top, left, right, bottom}).
 */
function toBoundingBox(raw: unknown): IIDPBoundingBox | null {
  if (!raw || typeof raw !== 'object') return null;

  const obj = raw as Record<string, unknown>;

  if ('x' in obj && 'y' in obj && 'width' in obj && 'height' in obj) {
    return {
      x: Number(obj.x),
      y: Number(obj.y),
      width: Number(obj.width),
      height: Number(obj.height),
    };
  }

  if ('top' in obj && 'left' in obj && 'right' in obj && 'bottom' in obj) {
    const top = Number(obj.top);
    const left = Number(obj.left);
    const right = Number(obj.right);
    const bottom = Number(obj.bottom);
    return { x: left, y: top, width: right - left, height: bottom - top };
  }

  return null;
}

/**
 * Averages a per-column confidence array into a single 0-100 score.
 * Falls back to 0 if the input is not an array or is empty.
 */
function averageConfidence(raw: unknown): number {
  if (Array.isArray(raw) && raw.length > 0) {
    const sum = raw.reduce((acc: number, v: unknown) => acc + Number(v), 0);
    return Math.round(sum / raw.length);
  }
  if (typeof raw === 'number') return raw;
  return 0;
}

/**
 * Extracts the common document-level properties shared by most normalizers:
 * page number, bounding box, scalar confidence, and source document name.
 * Falls back to the legacy `location` field (top/left/right/bottom/page_index)
 * when `bounding_box` is absent.
 */
function docMeta(raw: RawElement) {
  const bbox = toBoundingBox(raw.bounding_box) ?? toBoundingBox(raw.location);
  const loc = raw.location as Record<string, unknown> | undefined;
  const pageNumber =
    raw.page_number != null
      ? Number(raw.page_number)
      : loc?.page_index != null
        ? Number(loc.page_index) + 1
        : 1;

  return {
    page_number: pageNumber,
    bounding_box: bbox,
    confidence: Number(raw.confidence ?? 0),
    document: String(raw.source_document ?? raw.document ?? ''),
  };
}

function docIndex(raw: RawElement): number | null {
  return raw.source_document_index != null
    ? Number(raw.source_document_index)
    : null;
}

/**
 * Normalizes a book-idp `DocumentField` (element_type "document_field")
 * into the legacy `IIDPExtractedField` shape.
 */
export function normalizeDocumentField(raw: RawElement): IIDPExtractedField {
  return {
    element_type: EIDPElementType.EXTRACTED_FIELD,
    name: String(raw.name ?? ''),
    values: Array.isArray(raw.values) ? raw.values : [],
    ...docMeta(raw),
    is_handwritten: Boolean(raw.is_handwritten),
    verification: raw.verification ?? null,
    document_index: docIndex(raw),
  };
}

/**
 * Normalizes a book-idp `TextField` (element_type "text_field")
 * into the legacy `IIDPExtractedField` shape.
 *
 * Text fields come from text-input extraction (not documents) so they
 * have no page number or bounding box — only character-offset `location`
 * and `source_text_index`. Those are stored in `document_index` so the
 * UI can still correlate fields with their source text.
 */
export function normalizeTextField(raw: RawElement): IIDPExtractedField {
  return {
    element_type: EIDPElementType.EXTRACTED_FIELD,
    name: String(raw.name ?? ''),
    values: Array.isArray(raw.values) ? raw.values : [],
    page_number: 1,
    bounding_box: null,
    confidence: Number(raw.confidence ?? 0),
    document: '',
    document_index:
      raw.source_text_index != null ? Number(raw.source_text_index) : null,
  };
}

/**
 * Extracts the common table content (title, headers, rows, dimensions) from
 * a raw element. Handles both nested `table` property and flat legacy format.
 */
function parseTableContent(raw: RawElement) {
  const nested =
    raw.table && typeof raw.table === 'object'
      ? (raw.table as Record<string, unknown>)
      : raw;

  return {
    title: nested.title != null ? String(nested.title) : undefined,
    headers: Array.isArray(nested.headers) ? nested.headers.map(String) : [],
    rows: Array.isArray(nested.rows)
      ? (nested.rows as unknown[][]).map(row =>
          Array.isArray(row) ? row.map(cell => String(cell ?? '')) : [],
        )
      : [],
    num_rows: Number(nested.num_rows ?? 0),
    num_cols: Number(nested.num_cols ?? 0),
  };
}

/**
 * Normalizes a `DocumentIDPTable` or legacy `table` element into the
 * `IIDPTable` shape. New contract nests data under a `table` property;
 * legacy tables have `headers`/`rows` directly at the top level.
 */
export function normalizeDocumentTable(raw: RawElement): IIDPTable {
  return {
    element_type: EIDPElementType.TABLE,
    ...parseTableContent(raw),
    ...docMeta(raw),
    confidence: averageConfidence(raw.confidence),
    is_handwritten: Boolean(raw.is_handwritten),
    verification: raw.verification ?? null,
    document_index: docIndex(raw),
  };
}

/**
 * Normalizes a book-idp `TextIDPTable` (element_type "text_idp_table")
 * into the legacy `IIDPTable` shape by flattening the nested `table` object.
 *
 * Text tables come from text-input extraction so they have no page number
 * or bounding box — only character-offset `location` and `source_text_index`.
 */
export function normalizeTextTable(raw: RawElement): IIDPTable {
  return {
    element_type: EIDPElementType.TABLE,
    ...parseTableContent(raw),
    page_number: 1,
    bounding_box: null,
    confidence: averageConfidence(raw.confidence),
    document: '',
    document_index:
      raw.source_text_index != null ? Number(raw.source_text_index) : null,
  };
}

/**
 * Normalizes a book-idp `DocumentKeyValuePair`
 * (element_type "document_key_value_pair") into the legacy `IIDPKeyValue` shape.
 */
export function normalizeDocumentKeyValuePair(raw: RawElement): IIDPKeyValue {
  return {
    element_type: EIDPElementType.KEY_VALUE,
    key: String(raw.key ?? ''),
    value: String(raw.value ?? ''),
    ...docMeta(raw),
    is_handwritten: Boolean(raw.is_handwritten),
    verification: raw.verification ?? null,
  };
}

/**
 * Normalizes a book-idp `DocumentText` (element_type "document_text")
 * into either the legacy `IIDPTextBlock` or `IIDPListBlock` shape depending
 * on whether the element carries list data.
 */
export function normalizeDocumentText(
  raw: RawElement,
): IIDPTextBlock | IIDPListBlock {
  const textType = String(raw.text_type ?? 'paragraph');
  const meta = docMeta(raw);

  if (textType === 'list' && raw.list_type) {
    return {
      element_type: EIDPElementType.LIST,
      list_type: String(raw.list_type),
      items: Array.isArray(raw.items) ? raw.items.map(String) : [],
      ...meta,
    };
  }

  return {
    element_type: EIDPElementType.TEXT,
    content: String(raw.content ?? ''),
    style:
      textType === 'heading' || textType === 'subheading'
        ? 'heading'
        : 'paragraph',
    level: raw.level != null ? Number(raw.level) : null,
    ...meta,
  };
}

/**
 * Normalizes a pre-unification `ExtractFieldResult` (no `element_type`,
 * uses `location` with top/left/right/bottom instead of `bounding_box`).
 */
function normalizeLegacyExtractedField(raw: RawElement): IIDPExtractedField {
  const loc = raw.location as Record<string, unknown> | undefined;
  const pageNumber = loc?.page_index != null ? Number(loc.page_index) + 1 : 1;

  return {
    element_type: EIDPElementType.EXTRACTED_FIELD,
    name: String(raw.name ?? ''),
    values: Array.isArray(raw.values) ? raw.values : [],
    page_number: pageNumber,
    bounding_box: toBoundingBox(loc ?? null),
    confidence: Number(raw.confidence ?? 0),
    document: String(raw.document ?? ''),
    document_index:
      raw.document_index != null ? Number(raw.document_index) : null,
  };
}

/**
 * Normalizes a single raw element into the legacy `IIDPField` union,
 * dispatching by `element_type`. Also handles pre-unification legacy fields
 * that have no `element_type` but carry `name`/`values`/`location`.
 * Returns `null` for unrecognized element types.
 */
export function normalizeElement(raw: RawElement): IIDPField | null {
  switch (raw.element_type) {
    case EIDPElementType.DOCUMENT_FIELD:
    case EIDPElementType.EXTRACTED_FIELD:
      return normalizeDocumentField(raw);

    case EIDPElementType.TEXT_FIELD:
      return normalizeTextField(raw);

    case EIDPElementType.DOCUMENT_IDP_TABLE:
    case EIDPElementType.IDP_TABLE:
    case EIDPElementType.TABLE:
      return normalizeDocumentTable(raw);

    case EIDPElementType.TEXT_IDP_TABLE:
      return normalizeTextTable(raw);

    case EIDPElementType.DOCUMENT_KEY_VALUE_PAIR:
    case EIDPElementType.KEY_VALUE:
      return normalizeDocumentKeyValuePair(raw);

    case EIDPElementType.DOCUMENT_TEXT:
    case EIDPElementType.TEXT:
    case EIDPElementType.LIST:
      return normalizeDocumentText(raw);

    default:
      if ('name' in raw && 'values' in raw) {
        return normalizeLegacyExtractedField(raw);
      }
      return null;
  }
}

/**
 * Normalizes an array of raw elements, filtering out any that could not
 * be mapped.
 */
export function normalizeElements(raws: unknown[]): IIDPField[] {
  return raws
    .filter((r): r is RawElement => r != null && typeof r === 'object')
    .map(normalizeElement)
    .filter((f): f is IIDPField => f !== null);
}
